import React, { useRef } from 'react';
import { Box, Typography, TextField } from '@mui/material';


export interface PersonalInfo {
  fullName: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  availableFrom: string;
  phoneNumber: string;
  email: string;
  birthYear: string;
  nationality: string;
  residencePermit: string;
  civilStatus: string;
}

export interface RecipientInfo {
  company: string;
  contactPerson: string;
  address: string;
  postalCode: string;
  city: string;
}

export interface MotivationTemplateData {
  personalInfo: PersonalInfo;
  recipientInfo?: RecipientInfo;
  title: string;
  greeting: string;
  motivationText: string;
  signature?: string;
  // Neue editierbare Felder
  senderName?: string;
  senderAddress?: string;
  senderPostalCode?: string;
  senderCity?: string;
  recipientCompany?: string;
  recipientContactPerson?: string;
  recipientAddress?: string;
  recipientPostalCode?: string;
  recipientCity?: string;
  closingText?: string;
  // Weitere editierbare Felder
  dateLocation?: string;
  signatureName?: string;
}

// Container-Style für konsistente Darstellung
const containerStyle = {
  maxWidth: '794px', // A4 Breite in Pixeln bei 96 DPI
  minHeight: '1123px', // A4 Höhe in Pixeln bei 96 DPI
  margin: '0 auto',
  padding: '40px', // Zurück zu 40px für professionelles Layout
  backgroundColor: '#ffffff',
  fontFamily: 'Calibri, Arial, sans-serif',
  fontSize: '12pt',
  lineHeight: 1.6,
  color: '#000000',
  boxSizing: 'border-box' as const,
  position: 'relative' as const
};

// Style für Seitenumbrüche
const pageBreakStyle = {
  position: 'absolute' as const,
  left: '0',
  right: '0',
  height: '2px',
  backgroundColor: '#ff6b6b',
  borderTop: '1px dashed #ff4757',
  borderBottom: '1px dashed #ff4757',
  zIndex: 10
};

// Style für A4-Seitenränder
const a4PageBorderStyle = {
  position: 'absolute' as const,
  top: '0',
  left: '0',
  right: '0',
  bottom: '0',
  border: '3px solid #999999',
  borderRadius: '0',
  pointerEvents: 'none' as const,
  zIndex: 1
};

  // Funktion zum Berechnen der Seitenumbrüche
const calculatePageBreaks = (contentHeight: number): number[] => {
  const pageHeight = 1123 - 80; // A4 Höhe minus Padding (40px oben + 40px unten)
  const tolerance = 20; // Reduzierte Toleranz für frühere Warnung
  const breaks: number[] = [];
  
  // Seitenumbruch anzeigen, wenn der Inhalt über eine Seite hinausgeht
  if (contentHeight > pageHeight + tolerance) {
    let currentHeight = 0;
    while (currentHeight + pageHeight < contentHeight) {
      currentHeight += pageHeight;
      breaks.push(currentHeight);
    }
  }
  
  return breaks;
};

// Funktion zum Berechnen der tatsächlichen Inhaltshöhe mit Abständen
const calculateTotalContentHeight = (contentRef: React.RefObject<HTMLDivElement>): number => {
  if (!contentRef.current) return 0;
  
  const elements = contentRef.current.children;
  let totalHeight = 0;
  
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i] as HTMLElement;
    const rect = element.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(element);
    
    // Element-Höhe - WICHTIG: Verwende scrollHeight für Textfelder
    let elementHeight = rect.height;
    
    // Spezielle Behandlung für Textfelder mit Zeilenumbrüchen
    // Suche nach verschiedenen Textfeld-Typen
    const textarea = element.querySelector('textarea');
    const input = element.querySelector('input');
    const textField = element.querySelector('.MuiInputBase-input');
    const multilineInput = element.querySelector('[data-multiline="true"]');
    
    // Prüfe alle möglichen Textfeld-Typen
    let textFieldElement = textarea || input || textField || multilineInput;
    
    if (textFieldElement) {
      // Für Textfelder verwende scrollHeight, um Zeilenumbrüche zu berücksichtigen
      const scrollHeight = (textFieldElement as any).scrollHeight || 0;
      const clientHeight = (textFieldElement as any).clientHeight || 0;
      const offsetHeight = (textFieldElement as any).offsetHeight || 0;
      
      // Spezielle Behandlung für Material-UI TextField
      if (textFieldElement.classList.contains('MuiInputBase-input')) {
        // Verwende scrollHeight für Material-UI TextField
        elementHeight = Math.max(elementHeight, scrollHeight);
        console.log(`Material-UI TextField ${i + 1}:`, {
          scrollHeight,
          clientHeight,
          offsetHeight,
          usedHeight: elementHeight,
          value: (textFieldElement as any).value?.substring(0, 50) + '...'
        });
      } else {
        // Verwende die größte Höhe für andere Textfelder
        elementHeight = Math.max(elementHeight, scrollHeight, clientHeight, offsetHeight);
      }
      
      console.log(`Textfeld ${i + 1} - Zeilenumbrüche erkannt:`, {
        elementType: textFieldElement.tagName,
        className: textFieldElement.className,
        rectHeight: rect.height,
        scrollHeight: scrollHeight,
        clientHeight: clientHeight,
        offsetHeight: offsetHeight,
        usedHeight: elementHeight,
        value: (textFieldElement as any).value || 'N/A'
      });
    }
    
    // Abstände (margin)
    const marginTop = parseFloat(computedStyle.marginTop) || 0;
    const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
    
    // Padding
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    
    // Gesamthöhe für dieses Element
    const elementTotalHeight = elementHeight + marginTop + marginBottom + paddingTop + paddingBottom;
    
    totalHeight += elementTotalHeight;
    
    console.log(`Element ${i + 1}:`, {
      tagName: element.tagName,
      className: element.className.split(' ')[0] || 'none',
      elementHeight,
      marginTop,
      marginBottom,
      paddingTop,
      paddingBottom,
      elementTotalHeight,
      cumulativeHeight: totalHeight,
      hasTextField: !!textFieldElement,
      textFieldType: textFieldElement ? textFieldElement.tagName : null,
      textFieldScrollHeight: textFieldElement ? (textFieldElement as any).scrollHeight : null
    });
  }
  
  return totalHeight;
};

// Funktion zum Formatieren des Datums
const formatDate = () => {
  const today = new Date();
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  };
  return today.toLocaleDateString('de-DE', options);
};

// React-Komponente für Preview
export const MotivationTemplateComponent: React.FC<{
  data: MotivationTemplateData;
  onTitleChange?: (text: string) => void;
  onGreetingChange?: (text: string) => void;
  onMotivationTextChange?: (text: string) => void;
  onSenderNameChange?: (text: string) => void;
  onSenderAddressChange?: (text: string) => void;
  onSenderPostalCodeChange?: (text: string) => void;
  onSenderCityChange?: (text: string) => void;
  onRecipientCompanyChange?: (text: string) => void;
  onRecipientContactPersonChange?: (text: string) => void;
  onRecipientAddressChange?: (text: string) => void;
  onRecipientPostalCodeChange?: (text: string) => void;
  onRecipientCityChange?: (text: string) => void;
  onClosingTextChange?: (text: string) => void;
  onDateLocationChange?: (text: string) => void;
  onSignatureNameChange?: (text: string) => void;
  showBorder?: boolean;
  'data-testid'?: string;
}> = ({ 
  data, 
  onTitleChange, 
  onGreetingChange, 
  onMotivationTextChange, 
  onSenderNameChange,
  onSenderAddressChange,
  onSenderPostalCodeChange,
  onSenderCityChange,
  onRecipientCompanyChange,
  onRecipientContactPersonChange,
  onRecipientAddressChange,
  onRecipientPostalCodeChange,
  onRecipientCityChange,
  onClosingTextChange,
  onDateLocationChange,
  onSignatureNameChange,
  showBorder = false,
  'data-testid': dataTestId
}) => {
  const { 
    personalInfo, 
    recipientInfo, 
    title, 
    greeting, 
    motivationText, 
    signature,
    senderName,
    senderAddress,
    senderPostalCode,
    senderCity,
    recipientCompany,
    recipientContactPerson,
    recipientAddress,
    recipientPostalCode,
    recipientCity,
    closingText,
    dateLocation,
    signatureName
  } = data;
  const [pageBreaks, setPageBreaks] = React.useState<number[]>([]);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Seitenumbruch-Berechnung für Browser Print
  const calculatePageBreaks = React.useCallback(() => {
    if (contentRef.current) {
      // Verwende den Hauptcontainer selbst als Content-Container
      const contentContainer = contentRef.current;
      
      if (!contentContainer) {
        console.log('❌ Kein Content-Container gefunden');
        setPageBreaks([]);
        return;
      }
      
      // Berechne die tatsächliche Höhe aller Elemente im Container
      let totalHeight = 0;
      const children = contentContainer.children;
      
      console.log('📄 SEITENUMBRUCH-BERECHNUNG:');
      console.log('- Content Container gefunden:', !!contentContainer);
      console.log('- Anzahl Kinder:', children.length);
      
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        const rect = child.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(child);
        
        // Prüfe ob das Element horizontal angeordnet ist (Flexbox row)
        const display = computedStyle.display;
        const flexDirection = computedStyle.flexDirection;
        const isHorizontalLayout = display === 'flex' && flexDirection === 'row';
        
        // Element-Höhe
        let elementHeight = rect.height;
        
        // Spezielle Behandlung für contentEditable Elemente
        const contentEditableElements = child.querySelectorAll('[contenteditable="true"]');
        if (contentEditableElements.length > 0) {
          for (let j = 0; j < contentEditableElements.length; j++) {
            const editable = contentEditableElements[j] as HTMLElement;
            const editableRect = editable.getBoundingClientRect();
            const editableScrollHeight = editable.scrollHeight;
            const editableClientHeight = editable.clientHeight;
            const editableOffsetHeight = editable.offsetHeight;
            
            // Verwende die größte Höhe für contentEditable Elemente
            const editableHeight = Math.max(editableRect.height, editableScrollHeight, editableClientHeight, editableOffsetHeight);
            elementHeight = Math.max(elementHeight, editableHeight);
            
            console.log(`  - ContentEditable Element:`, {
              rectHeight: editableRect.height + 'px',
              scrollHeight: editableScrollHeight + 'px',
              clientHeight: editableClientHeight + 'px',
              offsetHeight: editableOffsetHeight + 'px',
              usedHeight: editableHeight + 'px'
            });
          }
        }
        
        // Abstände
        const marginTop = parseFloat(computedStyle.marginTop) || 0;
        const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
        const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
        const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
        
        const elementTotalHeight = elementHeight + marginTop + marginBottom + paddingTop + paddingBottom;
        
        // Nur die Höhe des Containers hinzufügen, nicht die Höhe der horizontalen Kinder
        if (isHorizontalLayout) {
          // Bei horizontalen Layouts nur die Container-Höhe berücksichtigen
          totalHeight += elementTotalHeight;
          console.log(`- Element ${i + 1} (${child.tagName}) - HORIZONTAL:`, {
            elementHeight: elementHeight + 'px',
            marginTop: marginTop + 'px',
            marginBottom: marginBottom + 'px',
            paddingTop: paddingTop + 'px',
            paddingBottom: paddingBottom + 'px',
            totalHeight: elementTotalHeight + 'px',
            cumulativeHeight: totalHeight + 'px',
            isHorizontalLayout: true
          });
        } else {
          // Bei vertikalen Layouts normal berechnen
          totalHeight += elementTotalHeight;
          console.log(`- Element ${i + 1} (${child.tagName}) - VERTIKAL:`, {
            elementHeight: elementHeight + 'px',
            marginTop: marginTop + 'px',
            marginBottom: marginBottom + 'px',
            paddingTop: paddingTop + 'px',
            paddingBottom: paddingBottom + 'px',
            totalHeight: elementTotalHeight + 'px',
            cumulativeHeight: totalHeight + 'px',
            isHorizontalLayout: false,
            hasContentEditable: contentEditableElements.length > 0
          });
        }
      }
      
      // Fallback: Verwende auch die Container-Höhen
      const contentHeight = contentContainer.scrollHeight;
      const clientHeight = contentContainer.clientHeight;
      const offsetHeight = (contentContainer as HTMLElement).offsetHeight;
      
      // Verwende die größte Höhe und ziehe 30mm ab für restriktivere Berechnung
      const rawHeight = Math.max(totalHeight, contentHeight, clientHeight, offsetHeight);
      const heightReduction = 30 * 3.78; // 30mm in px (erhöht von 15mm)
      const actualHeight = Math.max(0, rawHeight - heightReduction);
      
      // Genauere Berechnung für Print-Layout
      // A4: 210mm x 297mm
      // Word-Standard-Ränder: 25.4mm oben, 25.4mm unten, 25.4mm links, 25.4mm rechts
      // Verfügbare Höhe: 297mm - 50.8mm = 246.2mm
      // Konvertierung zu px bei 96 DPI: 246.2mm * 3.78 = ~930px
      // ABER: Wir verwenden bereits 25.4mm Padding im Container, also ist die verfügbare Höhe höher
      const availablePageHeight = 297 - 50.8; // 246.2mm (A4 minus Ränder)
      const pageHeightPx = availablePageHeight * 3.78; // ~930px
      const tolerance = 20; // Reduzierte Toleranz für restriktivere Berechnung (reduziert von 50px)
      
      // Korrekte Berechnung: Der Container hat bereits 25.4mm Padding
      // Die verfügbare Höhe ist also die Container-Höhe minus das Padding
      // Zusätzlich 15mm zur verfügbaren Höhe hinzufügen für restriktivere Berechnung (reduziert von 20mm)
      const containerHeight = 297 * 3.78; // A4 Höhe in px
      const containerPadding = 25.4 * 3.78 * 2; // Padding oben und unten in px
      const additionalHeight = 18 * 3.78; // 15mm zusätzliche Höhe in px (reduziert von 20mm um 5mm weniger Platz)
      const actualAvailableHeight = containerHeight - containerPadding + additionalHeight; // ~930px + 15mm
      
      console.log('- Berechnete Gesamthöhe (Elemente):', totalHeight, 'px');
      console.log('- scrollHeight:', contentHeight, 'px');
      console.log('- clientHeight:', clientHeight, 'px');
      console.log('- offsetHeight:', offsetHeight, 'px');
      console.log('- Roh-Höhe (rawHeight):', rawHeight, 'px');
      console.log('- Höhen-Reduktion (30mm):', heightReduction, 'px');
      console.log('- Verwendete Höhe (actualHeight):', actualHeight, 'px');
      console.log('- Container Höhe:', containerHeight, 'px');
      console.log('- Container Padding:', containerPadding, 'px');
      console.log('- Zusätzliche Höhe (5mm):', additionalHeight, 'px');
      console.log('- Verfügbare Höhe (korrekt):', actualAvailableHeight, 'px');
      
      const breaks: number[] = [];
      // Verwende die erhöhte Höhe nur für die Schwellwert-Berechnung
      const thresholdHeight = actualAvailableHeight + tolerance;
      // Für die Positionierung verwende die ursprüngliche Höhe ohne die zusätzliche Höhe
      const originalAvailableHeight = containerHeight - containerPadding;
      
      console.log('- Schwellwert (mit Toleranz):', thresholdHeight, 'px');
      console.log('- Über eine Seite?', actualHeight > thresholdHeight);
      console.log('- Differenz:', actualHeight - thresholdHeight, 'px');
      console.log('- Verwendete Höhe (für Positionierung):', actualAvailableHeight, 'px');
      
      if (actualHeight > thresholdHeight) {
        let currentHeight = actualAvailableHeight;
        while (currentHeight < actualHeight) {
          breaks.push(currentHeight);
          currentHeight += actualAvailableHeight;
        }
      }
      
      console.log('- Gefundene Seitenumbrüche:', breaks.length);
      console.log('- setPageBreaks aufgerufen mit:', breaks);
      setPageBreaks(breaks);
    }
  }, []);

  // Berechne Seitenumbrüche bei Änderungen
  React.useEffect(() => {
    console.log('🔄 useEffect triggered - recalculating page breaks');
    console.log('🔄 Current data values:', {
      title: title?.substring(0, 50) + '...',
      greeting: greeting?.substring(0, 50) + '...',
      motivationText: motivationText?.substring(0, 50) + '...',
      closingText: closingText?.substring(0, 50) + '...'
    });
    
    const timeoutId = setTimeout(() => {
      calculatePageBreaks();
    }, 300); // Erhöht auf 300ms für bessere DOM-Rendering-Zeit
    return () => clearTimeout(timeoutId);
  }, [
    personalInfo, 
    recipientInfo, 
    title, 
    greeting, 
    motivationText, 
    signature,
    senderName,
    senderAddress,
    senderPostalCode,
    senderCity,
    recipientCompany,
    recipientContactPerson,
    recipientAddress,
    recipientPostalCode,
    recipientCity,
    closingText,
    dateLocation,
    signatureName,
    calculatePageBreaks
  ]);

    return (
    <>
      

      <Box  
        ref={contentRef}
        data-testid={dataTestId}
        data-content="true"
        sx={{
          width: '210mm',           // A4-Breite
          minHeight: '297mm',       // A4-Höhe
          margin: '0 auto',
          padding: '25.4mm',        // Word-Standard Seitenabstände (1 Zoll)
          backgroundColor: '#ffffff',
          fontFamily: '"Times New Roman", Times, serif',
          fontSize: '12pt',
          lineHeight: 1.6,
          color: '#000000',
          boxSizing: 'border-box',
          border: showBorder ? '2px solid #cccccc' : '2px solid transparent',
          borderRadius: '8px',
          position: 'relative',
          overflow: 'hidden',       // Verhindert Überlauf
          '@media print': {
            '@page': {
              margin: '0mm',
              size: 'A4'
            },
            '&': {
              margin: '0mm',
              padding: '25.4mm'
            }
          }
        }}
      >

        
        {/* Seitenumbruch-Markierungen - nur für Preview, nicht für PDF */}
        {pageBreaks.map((breakPosition, index) => (
          <Box
            key={index}
            className="page-break-marker"
            sx={{
              position: 'absolute',
              left: '25.4mm',
              right: '25.4mm',
              top: `${breakPosition}px`,
              height: '4px',
              background: 'linear-gradient(90deg, #ff6b6b 0%, #ff8e8e 50%, #ff6b6b 100%)',
              border: '2px solid #d32f2f',
              borderStyle: 'dashed',
              zIndex: 10,
              boxShadow: '0 0 10px rgba(255, 107, 107, 0.6)',
              // Sicherstellen, dass die rote Linie nicht im PDF erscheint
              '@media print': {
                display: 'none !important'
              },
              // Weiße Linie entfernt - war über dem Text
              '&::after': {
                content: `"--- Seitenumbruch ${index + 2} ---"`,
                position: 'absolute',
                right: '10px',
                top: '-15px',
                backgroundColor: '#ff6b6b',
                color: 'white',
                fontSize: '12px',
                padding: '4px 10px',
                borderRadius: '8px',
                fontWeight: 'bold',
                border: '2px solid #d32f2f',
                boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
                zIndex: 11,
                // Sicherstellen, dass der Text auch nicht im PDF erscheint
                '@media print': {
                  display: 'none !important'
                }
              }
            }}
          />
        ))}

        {/* Inhalt ohne zusätzliches Padding */}
                <Box sx={{
          border: 'none',
          borderRadius: '0',
          position: 'relative',
          zIndex: 2,
          width: '159.2mm',         // Verfügbare Textbreite: 210mm - 50.8mm (25.4mm links + 25.4mm rechts)
          maxWidth: '159.2mm',      // Explizite Breitenbeschränkung
          overflow: 'hidden'        // Verhindert Überlauf
        }}>

          {/* Inhalt - Alle Inhalte werden für Seitenumbruch-Berechnung verwendet */}
        
        {/* Absender und zwei weitere DIVs nebeneinander */}
        <Box sx={{ 
          display: 'flex',
          gap: '15px',
          marginBottom: '8px'
        }}>
          {/* Absender (links) */}
          <Box sx={{ 
            flex: '1',
            border: showBorder ? '1px solid #e0e0e0' : '1px solid transparent',
            padding: showBorder ? '8px' : '8px',
            borderRadius: showBorder ? '4px' : '4px',
            backgroundColor: showBorder ? '#fafafa' : 'transparent',
            '&:hover': {
              border: '1px solid #cccccc',
              backgroundColor: '#f8f8f8'
            }
          }}>
           {onSenderNameChange ? (
             <div
               contentEditable
               suppressContentEditableWarning
               style={{
                 fontSize: '12pt',
                 fontFamily: 'Calibri, Arial, sans-serif',
                 color: '#555555',
                 lineHeight: '1.4',
                 outline: 'none',
                 border: '1px solid transparent',
                 cursor: 'text',
                 whiteSpace: 'pre-wrap',
                 minHeight: '20px'
               }}
               onFocus={(e) => {
                 e.currentTarget.style.border = '1px solid #ccc';
                 e.currentTarget.style.backgroundColor = '#f9f9f9';
               }}
               onInput={(e) => {
                 onSenderNameChange(e.currentTarget.textContent || '');
                 setTimeout(() => {
                   console.log('🔄 onInput triggered - recalculating page breaks');
                   calculatePageBreaks();
                 }, 100);
               }}
               onBlur={(e) => {
                 e.currentTarget.style.border = '1px solid transparent';
                 e.currentTarget.style.backgroundColor = 'transparent';
                 onSenderNameChange(e.currentTarget.textContent || '');
                 setTimeout(() => {
                   console.log('🔄 onBlur triggered - recalculating page breaks');
                   calculatePageBreaks();
                 }, 50);
               }}
             >
               {senderName || `${personalInfo.fullName || ''}\n${personalInfo.address || ''}\n${[personalInfo.postalCode, personalInfo.city].filter(Boolean).join(' ')}`.trim()}
             </div>
           ) : (
             (senderName || personalInfo.fullName || personalInfo.address || personalInfo.postalCode || personalInfo.city) && (
               <div
                 style={{
                   fontSize: '12pt',
                   fontFamily: 'Calibri, Arial, sans-serif',
                   color: '#555555',
                   whiteSpace: 'pre-wrap',
                   lineHeight: '1.4'
                 }}
               >
                 {senderName || `${personalInfo.fullName || ''}\n${personalInfo.address || ''}\n${[personalInfo.postalCode, personalInfo.city].filter(Boolean).join(' ')}`.trim()}
               </div>
             )
           )}
         </Box>

          {/* Zweites neues DIV rechts vom Absender */}
          <Box sx={{
            flex: '1',
            border: showBorder ? '1px solid #d0d0d0' : '1px solid transparent',
            padding: showBorder ? '6px' : '6px',
            borderRadius: showBorder ? '3px' : '3px',
            backgroundColor: showBorder ? '#f5f5f5' : 'transparent',
            minHeight: '20px'
          }} />

          {/* Drittes neues DIV rechts vom Absender */}
          <Box sx={{
            flex: '1',
            border: showBorder ? '1px solid #d0d0d0' : '1px solid transparent',
            padding: showBorder ? '6px' : '6px',
            borderRadius: showBorder ? '3px' : '3px',
            backgroundColor: showBorder ? '#f5f5f5' : 'transparent',
            minHeight: '20px'
          }} />
        </Box>

        {/* Empfänger und Datum */}
        {/* Zwei DIVs nebeneinander: Ein neues + Anschrift */}
        <Box sx={{
          display: 'flex',
          gap: '15px',
          marginBottom: '4px'
        }}>
          {/* Ein neues DIV links von Anschrift */}
          <Box sx={{
            flex: '1.22',
            border: showBorder ? '1px solid #d0d0d0' : '1px solid transparent',
            padding: showBorder ? '6px' : '6px',
            borderRadius: showBorder ? '3px' : '3px',
            backgroundColor: showBorder ? '#f5f5f5' : 'transparent',
            minHeight: '20px'
          }} />

          {/* Anschrift - immer anzeigen */}
          <Box sx={{
            flex: '1',
            border: showBorder ? '1px solid #d0d0d0' : '1px solid transparent',
            padding: showBorder ? '6px' : '6px',
            borderRadius: showBorder ? '3px' : '3px',
            backgroundColor: showBorder ? '#f5f5f5' : 'transparent',
            minHeight: '20px',
            height: 'auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              border: '1px solid #b0b0b0',
              backgroundColor: '#f0f0f0'
            }
          }}>
            {onRecipientCompanyChange ? (
              <div
                contentEditable
                suppressContentEditableWarning
                style={{
                  fontSize: '12pt',
                  fontFamily: 'Calibri, Arial, sans-serif',
                  color: '#555555',
                  lineHeight: '1.4',
                  outline: 'none',
                  border: '1px solid transparent',
                  cursor: 'text',
                  whiteSpace: 'pre-wrap',
                  minHeight: '20px'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.border = '1px solid #ccc';
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.border = '1px solid transparent';
                  e.currentTarget.style.backgroundColor = 'transparent';
                  onRecipientCompanyChange(e.currentTarget.textContent || '');
                  setTimeout(() => {
                    console.log('🔄 onBlur triggered - recalculating page breaks');
                    calculatePageBreaks();
                  }, 50);
                }}
              >
                {recipientInfo ? (recipientCompany || `${recipientInfo?.company || ''}\n${recipientInfo?.contactPerson || ''}`.trim()) : ''}
              </div>
            ) : (
              <div
                style={{
                  fontSize: '12pt',
                  fontFamily: 'Calibri, Arial, sans-serif',
                  color: '#555555',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.4',
                  minHeight: '20px'
                }}
              >
                {recipientInfo ? (recipientCompany || `${recipientInfo?.company || ''}\n${recipientInfo?.contactPerson || ''}`.trim()) : ''}
              </div>
            )}
            {/* Separates TextField für Empfänger-Adresse */}
            {onRecipientAddressChange && (
              <TextField
                value={recipientAddress ?? recipientInfo?.address ?? ''}
                onChange={(e) => {
                  // Speichere den Wert direkt, ohne Transformationen
                  onRecipientAddressChange(e.target.value);
                }}
                multiline
                rows={3}
                fullWidth
                variant="standard"
                sx={{
                  mt: 1,
                  flexShrink: 0,
                  '& .MuiInputBase-root': {
                    fontSize: '12pt',
                    fontFamily: 'Calibri, Arial, sans-serif',
                    color: '#555555',
                    borderBottom: 'none',
                    whiteSpace: 'pre-wrap',
                    alignItems: 'flex-start',
                    height: 'auto',
                    minHeight: 'auto',
                    maxHeight: '60px',
                    overflow: 'hidden'
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '12pt',
                    fontFamily: 'Calibri, Arial, sans-serif',
                    color: '#555555',
                    padding: '0',
                    whiteSpace: 'pre-wrap',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    maxHeight: '60px',
                    lineHeight: '1.4',
                    resize: 'none'
                  },
                  '& .MuiInput-underline:before': {
                    borderBottom: 'none'
                  },
                  '& .MuiInput-underline:after': {
                    borderBottom: 'none'
                  },
                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    borderBottom: 'none'
                  }
                }}
              />
            )}
            {!onRecipientAddressChange && recipientAddress && (
              <div
                style={{
                  fontSize: '12pt',
                  fontFamily: 'Calibri, Arial, sans-serif',
                  color: '#555555',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.4',
                  marginTop: '8px'
                }}
              >
                {recipientAddress}
              </div>
            )}
            {!onRecipientAddressChange && !recipientAddress && recipientInfo?.address && (
              <div
                style={{
                  fontSize: '12pt',
                  fontFamily: 'Calibri, Arial, sans-serif',
                  color: '#555555',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.4',
                  marginTop: '8px'
                }}
              >
                {recipientInfo.address}
              </div>
            )}
          </Box>
        </Box>

                          {/* Zwei DIVs nebeneinander: Ein neues + Datum/Ort */}
              <Box sx={{
                display: 'flex',
                gap: '15px',
                marginTop: '4px'
              }}>
                {/* Ein neues DIV links von Datum/Ort */}
                <Box sx={{
                  flex: '1.22',
                  border: showBorder ? '1px solid #d0d0d0' : '1px solid transparent',
                  padding: showBorder ? '6px' : '6px',
                  borderRadius: showBorder ? '3px' : '3px',
                  backgroundColor: showBorder ? '#f5f5f5' : 'transparent',
                  minHeight: '20px'
                }} />

                {/* Datum und Ort */}
                <Box sx={{
                  flex: '1',
                  border: showBorder ? '1px solid #d0d0d0' : '1px solid transparent',
                  padding: showBorder ? '6px' : '6px',
                  borderRadius: showBorder ? '3px' : '3px',
                  backgroundColor: showBorder ? '#f5f5f5' : 'transparent',
                  textAlign: 'left',
                  '&:hover': {
                    border: '1px solid #b0b0b0',
                    backgroundColor: '#f0f0f0'
                  }
                }}>
                  {onDateLocationChange ? (
                    <TextField
                      value={dateLocation || `${personalInfo.city}, ${formatDate()}`}
                      onChange={(e) => {
                        onDateLocationChange(e.target.value);
                        setTimeout(() => {
                          console.log('🔄 onBlur triggered - recalculating page breaks');
                          calculatePageBreaks();
                        }, 50);
                      }}
                      variant="standard"
                      fullWidth
                      sx={{
                        '& .MuiInputBase-root': {
                          fontSize: '12pt',
                          fontFamily: 'Calibri, Arial, sans-serif',
                          color: '#555555',
                          borderBottom: 'none'
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '12pt',
                          fontFamily: 'Calibri, Arial, sans-serif',
                          color: '#555555',
                          padding: '0'
                        },
                        '& .MuiInput-underline:before': {
                          borderBottom: 'none'
                        },
                        '& .MuiInput-underline:after': {
                          borderBottom: 'none'
                        },
                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                          borderBottom: 'none'
                        }
                      }}
                    />
                  ) : (
                    <Typography sx={{
                      fontSize: '12pt',
                      fontFamily: 'Calibri, Arial, sans-serif',
                      color: '#555555',
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                        border: '1px solid #d0d0d0'
                      }
                    }}>
                      {dateLocation || `${personalInfo.city}, ${formatDate()}`}
                    </Typography>
                  )}
                </Box>
        </Box>
        </Box>

                                                       {/* Betreff - AI-generiert */}
         <Box sx={{ 
           marginTop: '20px', 
           marginBottom: '10px',
           border: showBorder ? '1px solid #e0e0e0' : '1px solid transparent',
           padding: showBorder ? '8px' : '8px',
           borderRadius: showBorder ? '4px' : '4px',
           backgroundColor: showBorder ? '#fafafa' : 'transparent',
           '&:hover': {
             border: '1px solid #cccccc',
             backgroundColor: '#f8f8f8'
           }
         }}>
                  {onTitleChange ? (
            <div
              contentEditable
              suppressContentEditableWarning
              style={{
                fontSize: '12pt',
                fontWeight: 'bold',
                fontFamily: 'Calibri, Arial, sans-serif',
                color: '#000000',
                padding: '0',
                outline: 'none',
                border: '1px solid transparent',
                cursor: 'text',
                whiteSpace: 'pre-wrap'
              }}
              onFocus={(e) => {
                e.currentTarget.style.border = '1px solid #ccc';
                e.currentTarget.style.backgroundColor = '#f9f9f9';
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = '1px solid transparent';
                e.currentTarget.style.backgroundColor = 'transparent';
                onTitleChange(e.currentTarget.textContent || '');
                setTimeout(() => {
                  console.log('🔄 onBlur triggered - recalculating page breaks');
                  calculatePageBreaks();
                }, 50);
              }}
            >
              {title}
            </div>
          ) : (
            <div
              style={{
                fontSize: '12pt',
                fontWeight: 'bold',
                fontFamily: 'Calibri, Arial, sans-serif',
                color: '#000000',
                padding: '0'
              }}
            >
              {title}
            </div>
          )}
       </Box>

                                         {/* Anrede - AI-generiert */}
         <Box sx={{ 
           marginBottom: '10px',
           border: showBorder ? '1px solid #e0e0e0' : '1px solid transparent',
           padding: showBorder ? '8px' : '8px',
           borderRadius: showBorder ? '4px' : '4px',
           backgroundColor: showBorder ? '#fafafa' : 'transparent',
           '&:hover': {
             border: '1px solid #cccccc',
             backgroundColor: '#f8f8f8'
           }
         }}>
                   {onGreetingChange ? (
             <div
               contentEditable
               suppressContentEditableWarning
               style={{
                 fontSize: '12pt',
                 fontFamily: 'Calibri, Arial, sans-serif',
                 color: '#000000',
                 padding: '0',
                 outline: 'none',
                 border: '1px solid transparent',
                 cursor: 'text',
                 whiteSpace: 'pre-wrap'
               }}
               onFocus={(e) => {
                 e.currentTarget.style.border = '1px solid #ccc';
                 e.currentTarget.style.backgroundColor = '#f9f9f9';
               }}
               onBlur={(e) => {
                 e.currentTarget.style.border = '1px solid transparent';
                 e.currentTarget.style.backgroundColor = 'transparent';
                 onGreetingChange(e.currentTarget.textContent || '');
                 setTimeout(() => {
                   console.log('🔄 onBlur triggered - recalculating page breaks');
                   calculatePageBreaks();
                 }, 50);
               }}
             >
               {greeting}
             </div>
           ) : (
             <div
               style={{
                 fontSize: '12pt',
                 fontFamily: 'Calibri, Arial, sans-serif',
                 color: '#000000',
                 padding: '0'
               }}
             >
               {greeting}
             </div>
           )}
        </Box>

                           {/* Motivationsschreiben Text - AI-generiert */}
        <Box sx={{ 
          marginBottom: '10px',
          border: showBorder ? '1px solid #e0e0e0' : '1px solid transparent',
          padding: showBorder ? '8px' : '8px',
          borderRadius: showBorder ? '4px' : '4px',
          backgroundColor: showBorder ? '#fafafa' : 'transparent',
          width: '100%',              // Feste Breite
          maxWidth: '100%',          // Explizite Breitenbeschränkung
          overflow: 'hidden',        // Verhindert Überlauf
          boxSizing: 'border-box',   // Padding in Breite einrechnen
          '&:hover': {
            border: '1px solid #cccccc',
            backgroundColor: '#f8f8f8'
          }
        }}>

         {onMotivationTextChange ? (
           <div
             contentEditable
             suppressContentEditableWarning
             style={{
               fontSize: '12pt',
               lineHeight: 1.6,
               fontFamily: 'Calibri, Arial, sans-serif',
               color: '#000000',
               padding: '8px 0',
               minHeight: '4em',
               outline: 'none',
               border: '1px solid transparent',
               cursor: 'text',
               whiteSpace: 'pre-wrap'
             }}
             onFocus={(e) => {
               e.currentTarget.style.border = '1px solid #ccc';
               e.currentTarget.style.backgroundColor = '#f9f9f9';
             }}
             onBlur={(e) => {
               e.currentTarget.style.border = '1px solid transparent';
               e.currentTarget.style.backgroundColor = 'transparent';
               onMotivationTextChange(e.currentTarget.textContent || '');
               setTimeout(() => {
                 console.log('🔄 onBlur triggered - recalculating page breaks');
                 calculatePageBreaks();
               }, 50);
             }}
           >
             {motivationText}
           </div>
         ) : (
           <div
             style={{
               fontSize: '12pt',
               lineHeight: 1.6,
               fontFamily: 'Calibri, Arial, sans-serif',
               color: '#000000',
               padding: '8px 0'
             }}
           >
             {motivationText}
           </div>
         )}
      </Box>

                           {/* Schlussformel - editierbar */}
        <Box sx={{ 
          marginTop: '15px', 
          marginBottom: '-5px',
          border: showBorder ? '1px solid #e0e0e0' : '1px solid transparent',
          padding: showBorder ? '8px' : '8px',
          borderRadius: showBorder ? '4px' : '4px',
          backgroundColor: showBorder ? '#fafafa' : 'transparent',
          '&:hover': {
            border: '1px solid #cccccc',
            backgroundColor: '#f8f8f8'
          }
        }}>
        {onClosingTextChange ? (
          <div
            contentEditable
            suppressContentEditableWarning
            style={{
              fontSize: '12pt',
              fontFamily: 'Calibri, Arial, sans-serif',
              color: '#000000',
              padding: '0',
              outline: 'none',
              border: '1px solid transparent',
              cursor: 'text',
              whiteSpace: 'pre-wrap'
            }}
            onFocus={(e) => {
              e.currentTarget.style.border = '1px solid #ccc';
              e.currentTarget.style.backgroundColor = '#f9f9f9';
            }}
            onBlur={(e) => {
              e.currentTarget.style.border = '1px solid transparent';
              e.currentTarget.style.backgroundColor = 'transparent';
              onClosingTextChange(e.currentTarget.textContent || '');
              setTimeout(() => {
                console.log('🔄 onBlur triggered - recalculating page breaks');
                calculatePageBreaks();
              }, 50);
            }}
          >
            {closingText || 'Mit freundlichen Grüßen'}
          </div>
        ) : (
          <div
            style={{
              fontSize: '12pt',
              fontFamily: 'Calibri, Arial, sans-serif',
              color: '#000000',
              padding: '0'
            }}
          >
            {closingText || 'Mit freundlichen Grüßen'}
          </div>
        )}
      </Box>

             {/* Unterschrift - vom Template */}
       <Box sx={{ 
         marginBottom: '-15px',
         border: showBorder ? '1px solid #e0e0e0' : '1px solid transparent',
         padding: showBorder ? '8px' : '8px',
         borderRadius: showBorder ? '4px' : '4px',
         backgroundColor: showBorder ? '#fafafa' : 'transparent'
       }}>
        {signature && (
          <img
            src={signature}
            alt="Signatur"
            style={{
              maxHeight: '80px',
              maxWidth: '150px',
              objectFit: 'contain'
            }}
          />
        )}
       </Box>

             {/* Name - vom Template */}
       <Box sx={{ 
         marginBottom: 0,
         border: showBorder ? '1px solid #e0e0e0' : '1px solid transparent',
         padding: showBorder ? '8px' : '8px',
         borderRadius: showBorder ? '4px' : '4px',
         backgroundColor: showBorder ? '#fafafa' : 'transparent',
         '&:hover': {
           border: '1px solid #b0b0b0',
           backgroundColor: '#f0f0f0'
         }
       }}>
        {onSignatureNameChange ? (
          <div
            contentEditable
            suppressContentEditableWarning
            style={{
              fontSize: '12pt',
              fontFamily: 'Calibri, Arial, sans-serif',
              color: '#000000',
              padding: '0',
              outline: 'none',
              border: '1px solid transparent',
              cursor: 'text',
              whiteSpace: 'pre-wrap'
            }}
            onFocus={(e) => {
              e.currentTarget.style.border = '1px solid #ccc';
              e.currentTarget.style.backgroundColor = '#f9f9f9';
            }}
            onBlur={(e) => {
              e.currentTarget.style.border = '1px solid transparent';
              e.currentTarget.style.backgroundColor = 'transparent';
              onSignatureNameChange(e.currentTarget.textContent || '');
              setTimeout(() => {
                console.log('🔄 onBlur triggered - recalculating page breaks');
                calculatePageBreaks();
              }, 50);
            }}
          >
            {signatureName || personalInfo.fullName}
          </div>
        ) : (
          <div
            style={{
              fontSize: '12pt',
              fontFamily: 'Calibri, Arial, sans-serif',
              color: '#000000',
              padding: '0'
            }}
          >
            {signatureName || personalInfo.fullName}
          </div>
        )}
       </Box>
      </Box>
    </>
   );
 };


 